import { useEffect, useState } from "react"
import project from '@client/stores/projectStore'
import { doQuery } from '@/client/utils/useQuery'

export type UseViewDataArgs = {
    dataList: string,
    page: number,
    limit: number,
    sortField?: string,
    sortOrder?: string,
    search?: string,
}

export type UseViewDataResponse = {
    header: any[],
    mappedHeader: any[],
    rows: any[],
    rowCount: number,
    page: number,
    pageCount: number,
    total: number,
    filterTotal: number,
}

// Result for the useViewData hook, with generic response and error types
interface UseViewDataResult<TData = false, TError = false> {
    data: TData
    loading: boolean
    error: TError
}

export type WaitConditionProps<TArgs> = (args: TArgs) => boolean

export default function useViewData<TData extends UseViewDataResponse, TArgs extends UseViewDataArgs>(
    list: any, waitCondition: WaitConditionProps<TArgs>, args: TArgs)
    : UseViewDataResult<TData, Error | false> {
    const [data, setData] = useState<TData | false>(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | false>(false)

    useEffect(() => {
        const _waitForData = async () => {
            try {
                // build the final args for the query
                const finalArgs = {
                    ...args,
                    project: project.currentProject.value?._id,
                }

                // read data from server
                const res = (await doQuery(list, finalArgs)) as any
                setData(res?.data || [])
                setLoading(false)
            } catch (err: any) {
                if (err.response) {
                    console.log('data:', err.response.data)
                    console.log('status:', err.response.status)
                    console.log('headers:', err.response.headers)
                } else {
                    console.log('Error:', err.message)
                }
                setError(err)
                setLoading(false)
            }
        }

        setLoading(true)
        setError(false)
        if (waitCondition(args)) _waitForData()
    }, [args])

    return { data: data as TData, loading, error }
}
